'use client';

import React, { useEffect, useRef, useState } from 'react';
import * as fabric from 'fabric';
import type { Slide, SlideElement, ShapeType } from '../lib/types';
import { Sparkles, Image as ImageIcon, Trash2, Plus, PieChart } from 'lucide-react';
import { cn } from '../lib/utils';

const SHAPE_PATHS: Record<string, string> = {
    triangle: 'M 12,4 L 20,18 H 4 L 12,4 Z',
    rightTriangle: 'M 4,4 V 20 H 20 L 4,4 Z',
    rhombus: 'M 12,4 L 20,12 L 12,20 L 4,12 L 12,4 Z',
    parallelogram: 'M 7,4 H 21 L 17,20 H 3 L 7,4 Z',
    trapezoid: 'M 6,4 H 18 L 21,20 H 3 L 6,4 Z',
    pentagon: 'M 12,3 L 21,9 V 19 H 3 V 9 L 12,3 Z',
    hexagon: 'M 12,3 L 20,7.5 V 16.5 L 12,21 L 4,16.5 V 7.5 L 12,3 Z',
    heptagon: 'M 12,3 L 18.5,6 L 21,13 L 16,20 H 8 L 3,13 L 5.5,6 L 12,3 Z',
    octagon: 'M 8.5,3 H 15.5 L 21,8.5 V 15.5 L 15.5,21 H 8.5 L 3,15.5 V 8.5 L 8.5,3 Z',
    heart: 'M 12,21.35 L 10.55,20.03 C 5.4,15.36 2,12.28 2,8.5 2,5.42 4.42,3 7.5,3 C 9.24,3 10.91,3.81 12,5.09 C 13.09,3.81 14.76,3 16.5,3 C 19.58,3 22,5.42 22,8.5 C 22,12.28 18.6,15.36 13.45,20.04 L 12,21.35 Z',
    smiley: 'M 12,3 A 9,9 0 1,0 12,21 A 9,9 0 1,0 12,3 M 9,9 A 1.5,1.5 0 1,1 9,10.5 A 1.5,1.5 0 1,1 9,9 M 15,9 A 1.5,1.5 0 1,1 15,10.5 A 1.5,1.5 0 1,1 15,9 M 8,15 C 8,15 9.5,17 12,17 C 14.5,17 16,15 16,15',
    sun: 'M 12,7 A 5,5 0 1,1 12,17 A 5,5 0 1,1 12,7 M 12,2 V 4 M 12,20 V 22 M 4,12 H 2 M 22,12 H 20 M 19.07,4.93 L 17.66,6.34 M 6.34,17.66 L 4.93,19.07 M 4.93,4.93 L 6.34,6.34 M 17.66,17.66 L 19.07,19.07',
    moon: 'M 12,3 A 9,9 0 1,0 12,21 A 7,7 0 0,1 12,3 Z',
    circle: 'M 12,3 A 9,9 0 1,1 12,21 A 9,9 0 1,1 12,3 Z',
    arrowRight: 'M 4,11 H 15.5 L 12,7.5 L 13.5,6 L 19.5,12 L 13.5,18 L 12,16.5 L 15.5,13 H 4 Z',
    arrowLeft: 'M 20,11 H 8.5 L 12,7.5 L 10.5,6 L 4.5,12 L 10.5,18 L 12,16.5 L 8.5,13 H 20 Z',
    arrowUp: 'M 13,20 V 8.5 L 16.5,12 L 18,10.5 L 12,4.5 L 6,10.5 L 7.5,12 L 11,8.5 V 20 Z',
    arrowDown: 'M 11,4 V 15.5 L 7.5,12 L 6,13.5 L 12,19.5 L 18,13.5 L 16.5,12 L 13,15.5 V 4 Z',
    arrowLeftRight: 'M 4,12 L 8,8 V 11 H 16 V 8 L 20,12 L 16,16 V 13 H 8 V 16 L 4,12 Z',
    arrowUpDown: 'M 12,4 L 8,8 H 11 V 16 H 8 L 12,20 L 16,16 H 13 V 8 H 16 L 12,4 Z',
    star4: 'M 12,2 L 14,10 L 22,12 L 14,14 L 12,22 L 10,14 L 2,12 L 10,10 L 12,2 Z',
    star5: 'M 12,2 L 14.5,9.5 H 22 L 16,14 L 18.5,21.5 L 12,17 L 5.5,21.5 L 8,14 L 2,9.5 H 9.5 L 12,2 Z',
    star6: 'M 12,2 L 15,7 L 21,6 L 17,10.5 L 19,17 L 12,14 L 5,17 L 7,10.5 L 3,6 L 9,7 L 12,2 Z',
    star8: 'M 12,2 L 14,6 L 18,4 L 16,8 L 20,10 L 16,12 L 18,16 L 14,14 L 12,18 L 10,14 L 6,16 L 8,12 L 4,10 L 8,8 L 6,4 L 10,6 L 12,2 Z',
    cloud: 'M 17,19 C 14.8,19 13.1,17.7 12.3,15.8 C 11.4,16 10.5,16.2 9.6,16.2 C 7,16.2 4.7,14.6 3.7,12.3 C 1.5,11.8 0,9.9 0,7.6 C 0,5 2.1,2.9 4.7,2.9 C 5.3,2.9 5.8,3 6.3,3.1 C 7.6,1.4 9.6,0.3 11.9,0.3 C 12.5,0.3 13.1,0.4 13.7,0.5 C 15,1.9 15.9,3.7 16,5.7 C 18.2,6.1 20,8.1 20,10.5 C 20,13.2 17.8,15.4 15.1,15.4 C 14.8,15.4 14.5,15.4 14.2,15.3 C 14.2,15.4 14.2,15.4 14.2,15.5 C 14.2,17.4 15.5,19 17,19 Z',
    lightning: 'M 13,2 L 3,14 H 12 V 22 L 22,10 H 13 L 16,2 Z',
    plus: 'M 19,13 H 13 V 19 H 11 V 13 H 5 V 11 H 11 V 5 H 13 V 11 H 19 V 13 Z',
    minus: 'M 19,13 H 5 V 11 H 19 V 13 Z',
    multiply: 'M 19,6.41 L 17.59,5 L 12,10.59 L 6.41,5 L 5,6.41 L 10.59,12 L 5,17.59 L 6.41,19 L 12,13.41 L 17.59,19 L 19,17.59 L 13.41,12 L 19,6.41 Z',
    divide: 'M 12,7 A 2,2 0 1,1 12,11 A 2,2 0 1,1 12,7 M 12,17 A 2,2 0 1,1 12,21 A 2,2 0 1,1 12,17 M 5,11 H 19 V 13 H 5 V 11 Z',
    equal: 'M 19,10 H 5 V 8 H 19 V 10 M 19,16 H 5 V 14 H 19 V 16 Z'
};

const getBulletContent = (listType: string | undefined, isBulleted: boolean | undefined, isNumbered: boolean | undefined, index: number): string => {
    if (isBulleted) {
        switch (listType) {
            case 'bullet-disc': return '•';
            case 'bullet-circle': return '○';
            case 'bullet-square': return '■';
            case 'bullet-square-outlined': return '□';
            case 'bullet-diamond': return '◆';
            case 'bullet-arrow': return '➤';
            case 'bullet-check': return '✓';
            case 'none': return '';
            default: return '•'; // Default to disc
        }
    } else if (isNumbered) {
        const n = index + 1;
        switch (listType) {
            case 'number-decimal': return `${n}.`;
            case 'number-decimal-paren': return `${n})`;
            case 'number-roman-upper': return toRoman(n) + '.';
            case 'number-roman-lower': return toRoman(n).toLowerCase() + '.';
            case 'number-alpha-upper': return toAlpha(n) + '.';
            case 'number-alpha-lower': return toAlpha(n).toLowerCase() + '.';
            case 'number-alpha-lower-paren': return toAlpha(n).toLowerCase() + ')';
            case 'none': return '';
            default: return `${n}.`; // Default to decimal
        }
    }
    return '';
};

const toRoman = (num: number): string => {
    const lookup: { [key: string]: number } = { M: 1000, CM: 900, D: 500, CD: 400, C: 100, XC: 90, L: 50, XL: 40, X: 10, IX: 9, V: 5, IV: 4, I: 1 };
    let roman = '';
    for (const i in lookup) {
        while (num >= lookup[i]) {
            roman += i;
            num -= lookup[i];
        }
    }
    return roman;
};

const toAlpha = (num: number): string => {
    return String.fromCharCode(64 + num);
};

class CustomTextbox extends fabric.Textbox {
    listType?: string;
    isBulleted?: boolean;
    isNumbered?: boolean;

    _renderTextLine(method: any, ctx: any, line: string[], left: number, top: number, lineIndex: number) {
        super._renderTextLine(method, ctx, line, left, top, lineIndex);

        if ((this.isBulleted || this.isNumbered) && this.listType !== 'none') {
            const bulletText = getBulletContent(this.listType, this.isBulleted, this.isNumbered, lineIndex);
            if (bulletText) {
                ctx.save();
                const fontSize = this.fontSize || 22;
                // Reduce gap: -1.0em instead of -1.5em for tighter alignment
                const xOffset = -fontSize * 1.0;

                // Reduce bullet size to 60% of text size
                const bulletSize = fontSize * 0.6;
                ctx.font = `${this.fontStyle} ${this.fontWeight} ${bulletSize}px ${this.fontFamily}`;
                ctx.fillStyle = this.fill;

                // Move bullet up to vertically center it (adjusted for smaller bullet size)
                // User requested "further up" from 0.20, so trying 0.35 to hit the visual center
                ctx.fillText(bulletText, left + xOffset, top - (fontSize * 0.35));
                ctx.restore();
            }
        }
    }
}

interface CanvasProps {
    slide: Slide;
    onElementUpdate: (elementId: string, updates: Partial<SlideElement>) => void;
    onSelect: (id: string | null) => void;
    uiScale: number;
    onSlashCommand: (
        position: { x: number; y: number },
        mode?: 'default' | 'textbox' | 'text-selection',
        context?: {
            fontFamily?: string;
            fontSize?: number;
            isBold?: boolean;
            isItalic?: boolean;
            isBulleted?: boolean;
            textAlign?: 'left' | 'center' | 'right' | 'justify';
            color?: string;
        }
    ) => void;
    selectedElementId: string | null;
    onAiGenerateClick: () => void;
    onAiGenerateInfographicClick: () => void;
    onUploadImageClick: () => void;
    onDeleteElement: () => void;
}

export const EditorCanvas: React.FC<CanvasProps> = ({
    slide, onElementUpdate, onSelect, uiScale, onSlashCommand,
    selectedElementId, onAiGenerateClick, onAiGenerateInfographicClick, onUploadImageClick, onDeleteElement
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const fabricCanvas = useRef<fabric.Canvas | null>(null);
    const isInternalUpdate = useRef(false);

    const onElementUpdateRef = useRef(onElementUpdate);
    const onSelectRef = useRef(onSelect);
    const containerRef = useRef<HTMLDivElement>(null);
    const [dynamicScale, setDynamicScale] = useState(1);
    const [canvasReady, setCanvasReady] = useState(false);
    const mousePos = useRef({ x: 0, y: 0 });

    // Dynamic Scaling Logic
    useEffect(() => {
        if (!containerRef.current) return;

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) {
                const { width: cw, height: ch } = entry.contentRect;
                // Base dimensions for the slide are 1200x675
                const scaleX = cw / 1200;
                const scaleY = ch / 675;
                // We use the limiting factor (min) and subtract a bit of margin (0.9)
                const scale = Math.min(scaleX, scaleY) * 0.95;
                setDynamicScale(scale);
            }
        });

        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        onElementUpdateRef.current = onElementUpdate;
        onSelectRef.current = onSelect;
    }, [onElementUpdate, onSelect]);

    useEffect(() => {
        if (canvasRef.current && !fabricCanvas.current) {
            const canvas = new fabric.Canvas(canvasRef.current, {
                width: 1200,
                height: 675,
                backgroundColor: 'white',
                selection: true,
                preserveObjectStacking: true
            });

            fabricCanvas.current = canvas;
            setCanvasReady(true);

            const clamp = (obj: fabric.Object) => {
                if (obj.left! < 0) obj.set('left', 0);
                if (obj.top! < 0) obj.set('top', 0);
                const bound = obj.getBoundingRect();
                if (bound.left + bound.width > (canvas.width || 1200)) obj.set('left', (canvas.width || 1200) - bound.width);
                if (bound.top + bound.height > (canvas.height || 675)) obj.set('top', (canvas.height || 675) - bound.height);
            };

            canvas.on('object:moving', (e) => clamp(e.target!));
            canvas.on('object:scaling', (e) => clamp(e.target!));

            const handleUpdate = (e: any) => {
                if (isInternalUpdate.current) return;
                const obj = e.target as any;
                const elementId = obj.get('data')?.id;
                if (obj && elementId) {
                    onElementUpdateRef.current(elementId, {
                        x: obj.left,
                        y: obj.top,
                        width: obj.getScaledWidth(),
                        height: obj.getScaledHeight(),
                        ...(obj.isType('textbox') ? {
                            content: (obj as fabric.Textbox).text,
                            textAlign: (obj as fabric.Textbox).textAlign as any,
                            fontSize: obj.fontSize,
                            fontFamily: obj.fontFamily,
                            color: obj.fill as string
                        } : {
                            fill: obj.fill as string
                        })
                    });
                }
            };

            canvas.on('object:modified', handleUpdate);
            canvas.on('text:changed', handleUpdate);

            const getContext = (obj: any) => {
                if (!obj || !obj.isType('textbox')) return undefined;
                return {
                    fontFamily: obj.fontFamily,
                    fontSize: obj.fontSize,
                    isBold: obj.fontWeight === 'bold',
                    isItalic: obj.fontStyle === 'italic',
                    isUnderline: obj.underline,
                    textAlign: obj.textAlign as any,
                    color: obj.fill as string,
                    highlightColor: obj.textBackgroundColor as string,
                    // Check if it's bulleted (this might depend on how it's stored in fabric)
                    isBulleted: !!(obj as any).isBulleted,
                    isNumbered: !!(obj as any).isNumbered
                };
            };

            const triggerSlashMenu = (obj: any, isTextSelection = false) => {
                try {
                    const rect = canvasRef.current?.getBoundingClientRect();
                    if (!rect || !obj) return;

                    let x = 0, y = 0;

                    const hasMethod = (o: any, m: string) => typeof o === 'object' && o !== null && typeof o[m] === 'function';
                    const isTextbox = hasMethod(obj, 'isType') && obj.isType('textbox');
                    const isEditing = isTextbox && obj.isEditing;

                    if (isEditing) {
                        const getCoords = (o: any) => {
                            try {
                                const charIndex = o.selectionStart || 0;
                                if (hasMethod(o, 'getCursorCoords')) return o.getCursorCoords(charIndex);
                                if (hasMethod(o, '_getCursorCoords')) return o._getCursorCoords(charIndex);
                            } catch (e) { }
                            return null;
                        };

                        const coords = getCoords(obj);
                        if (coords) {
                            const canvasCoords = fabric.util.transformPoint(
                                new fabric.Point(coords.left, coords.top),
                                hasMethod(obj, 'calcTransformMatrix') ? obj.calcTransformMatrix() : [1, 0, 0, 1, 0, 0]
                            );
                            const vpt = canvas.viewportTransform || [1, 0, 0, 1, 0, 0];
                            const viewportCoords = fabric.util.transformPoint(canvasCoords, vpt);
                            x = rect.left + viewportCoords.x * dynamicScale;
                            y = rect.top + viewportCoords.y * dynamicScale;
                        } else {
                            const bound = hasMethod(obj, 'getBoundingRect') ? obj.getBoundingRect() : { left: 0, top: 0, width: 0, height: 0 };
                            x = rect.left + (bound.left + bound.width / 2) * dynamicScale;
                            y = rect.top + bound.top * dynamicScale;
                        }
                    } else {
                        const bound = hasMethod(obj, 'getBoundingRect') ? obj.getBoundingRect() : { left: mousePos.current.x, top: mousePos.current.y, width: 0, height: 0 };
                        x = rect.left + (bound.left + bound.width / 2) * dynamicScale;
                        y = rect.top + bound.top * dynamicScale;
                    }

                    if (x && y) {
                        const mode = isTextSelection ? 'text-selection' : (isTextbox ? 'textbox' : 'default');
                        let finalX = x;
                        let finalY = y;

                        if (mode === 'default') {
                            // Default command menu: position near the cursor/object without large centering offsets
                            finalX = x + 5;
                            finalY = y + 5;
                        } else {
                            // Horizontal toolbar: center above the text and offset upwards
                            finalX = x - 220;
                            finalY = y - 140;
                        }

                        onSlashCommand(
                            { x: finalX, y: finalY },
                            mode,
                            getContext(obj)
                        );
                    }
                } catch (err) {
                    console.error('Critical error in triggerSlashMenu:', err);
                }
            };

            const syncSelection = (e: any) => {
                const obj = e.selected?.[0] || e.target;
                if (obj) {
                    const elementId = (obj as any).get('data')?.id;
                    onSelectRef.current(elementId);

                    // Show slash menu on selection if it's a textbox
                    if (obj.isType('textbox') && !isInternalUpdate.current) {
                        triggerSlashMenu(obj);
                    }
                }
            };

            canvas.on('selection:created', syncSelection);
            canvas.on('selection:updated', syncSelection);
            canvas.on('selection:cleared', () => {
                onSelectRef.current(null);
                onSlashCommand({ x: 0, y: 0 }, 'default'); // Close menu or reset
            });

            // Handle text selection inside textbox
            canvas.on('text:selection:changed', (e: any) => {
                const obj = e.target;
                if (obj && obj.isType('textbox') && obj.selectionStart !== obj.selectionEnd) {
                    triggerSlashMenu(obj, true);
                }
            });

            canvas.on('mouse:move', (e: any) => {
                if (e.pointer) {
                    mousePos.current = { x: e.pointer.x, y: e.pointer.y };
                }
            });

            // Global keyboard navigation
            const handleGlobalKeyDown = (e: KeyboardEvent) => {
                const canvas = fabricCanvas.current;
                if (!canvas) return;
                const activeObj = canvas.getActiveObject();

                if (e.key === '/') {
                    e.preventDefault();
                    triggerSlashMenu(activeObj || { isType: () => false, getBoundingRect: () => ({ left: mousePos.current.x, top: mousePos.current.y, width: 0, height: 0 }) } as any);
                    return;
                }

                // Don't move if editing text
                if (activeObj && activeObj.isType('textbox') && (activeObj as any).isEditing) return;

                if (!activeObj) return;

                const step = e.shiftKey ? 10 : 1;
                let moved = false;

                switch (e.key) {
                    case 'ArrowLeft': activeObj.set('left', activeObj.left - step); moved = true; break;
                    case 'ArrowRight': activeObj.set('left', activeObj.left + step); moved = true; break;
                    case 'ArrowUp': activeObj.set('top', activeObj.top - step); moved = true; break;
                    case 'ArrowDown': activeObj.set('top', activeObj.top + step); moved = true; break;
                }

                if (moved) {
                    clamp(activeObj);
                    canvas.requestRenderAll();
                    handleUpdate({ target: activeObj });
                }
            };

            window.addEventListener('keydown', handleGlobalKeyDown);
            (canvas as any)._keyboardListener = handleGlobalKeyDown;
        }

        return () => {
            if (fabricCanvas.current) {
                const listener = (fabricCanvas.current as any)._keyboardListener;
                if (listener) window.removeEventListener('keydown', listener);
                fabricCanvas.current.dispose();
                fabricCanvas.current = null;
            }
        };
    }, []); // Empty dependency array means this only runs once!

    useEffect(() => {
        if (!fabricCanvas.current) return;
        const canvas = fabricCanvas.current;

        const syncElements = async () => {
            isInternalUpdate.current = true;

            const existingObjs = canvas.getObjects();
            const slideIds = new Set(slide.elements.map((e: SlideElement) => e.id));

            existingObjs.forEach(obj => {
                const id = (obj as any).get('data')?.id;
                if (id && !slideIds.has(id)) {
                    canvas.remove(obj);
                }
            });

            const sorted = [...slide.elements].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

            for (const el of sorted) {
                let obj = existingObjs.find((o: fabric.Object) => (o as any).get('data')?.id === el.id) as any;

                // Upgrade legacy Textbox to CustomTextbox to ensure list rendering works
                if (obj && obj.type === 'textbox' && !(obj instanceof CustomTextbox)) {
                    canvas.remove(obj);
                    obj = null;
                }

                if (obj) {
                    // CRITICAL: Skip syncing properties for the object currently being edited.
                    // If we update properties (like 'text') while Fabric.js is in editing mode,
                    // it resets the cursor position and can cause desync/focus loss.
                    if (obj === canvas.getActiveObject() && obj.isType('textbox') && (obj as fabric.Textbox).isEditing) {
                        continue;
                    }

                    const updates: any = {
                        left: el.x,
                        top: el.y,
                        opacity: el.opacity !== undefined ? el.opacity : 1,
                        zIndex: el.zIndex
                    };

                    if (el.type === 'text' && obj.isType('textbox')) {
                        updates.text = el.content || ' ';
                        updates.fontSize = el.fontSize || 22;
                        updates.fill = el.color || '#000000';
                        updates.textBackgroundColor = el.highlightColor || '';
                        updates.textAlign = el.textAlign || 'left';
                        updates.fontWeight = el.isBold ? 'bold' : 'normal';
                        updates.fontStyle = el.isItalic ? 'italic' : 'normal';
                        updates.underline = el.isUnderline || false;
                        updates.linethrough = el.isStrikethrough || false;
                        updates.charSpacing = el.charSpacing || 0;
                        updates.fontFamily = el.fontFamily || 'Arial';
                        (obj as any).isBulleted = el.isBulleted;
                        (obj as any).isNumbered = el.isNumbered;
                        (obj as any).listType = el.listType;
                        updates.width = Math.max(el.width || 50, 10);
                    } else if (el.type === 'shape') {
                        updates.fill = (el as any).fill || '#cccccc';
                        updates.scaleX = (el.width || 50) / (obj.width || 1);
                        updates.scaleY = (el.height || 50) / (obj.height || 1);
                    } else if (el.type === 'image') {
                        updates.scaleX = (el.width || 100) / (obj.width || 1);
                        updates.scaleY = (el.height || 100) / (obj.height || 1);
                    }

                    obj.set(updates);
                } else {
                    let newObj: fabric.Object | null = null;
                    const commonProps = {
                        left: el.x,
                        top: el.y,
                        data: { id: el.id },
                        originX: 'left' as const,
                        originY: 'top' as const,
                        opacity: el.opacity !== undefined ? el.opacity : 1
                    };

                    if (el.type === 'text') {
                        newObj = new CustomTextbox(el.content || ' ', {
                            ...commonProps,
                            width: Math.max(el.width || 200, 10),
                            fontSize: el.fontSize || 22,
                            fill: el.color || '#000000',
                            backgroundColor: el.highlightColor || '',
                            fontFamily: el.fontFamily || 'Inter, Arial, sans-serif',
                            textAlign: el.textAlign || 'left',
                            fontWeight: el.isBold ? 'bold' : 'normal',
                            fontStyle: el.isItalic ? 'italic' : 'normal',
                            underline: el.isUnderline || false,
                            linethrough: el.isStrikethrough || false,
                            charSpacing: el.charSpacing || 0,
                            splitByGrapheme: false,
                            objectCaching: false, // Critical: customized _renderTextLine draws outside bounding box
                        });
                        (newObj as any).isBulleted = el.isBulleted;
                        (newObj as any).isNumbered = el.isNumbered;
                        (newObj as any).listType = el.listType;
                    } else if (el.type === 'shape') {
                        const shapeFill = (el as any).fill || '#3b82f6';
                        if (el.shapeType === 'ellipse') {
                            newObj = new fabric.Circle({
                                ...commonProps,
                                radius: (el.width || 100) / 2,
                                scaleY: (el.height || 100) / (el.width || 100),
                                fill: shapeFill,
                            });
                        } else if (el.shapeType === 'rect') {
                            newObj = new fabric.Rect({
                                ...commonProps,
                                width: el.width || 100,
                                height: el.height || 100,
                                fill: shapeFill,
                            });
                        } else {
                            const pathData = SHAPE_PATHS[el.shapeType];
                            if (pathData) {
                                newObj = new fabric.Path(pathData, {
                                    ...commonProps,
                                    fill: shapeFill,
                                });
                                // Scale the path to fit the requested dimensions
                                // Paths in SHAPE_PATHS are roughly 24x24 based on viewbox
                                const p = newObj as fabric.Path;
                                p.set({
                                    scaleX: (el.width || 100) / (p.width || 1),
                                    scaleY: (el.height || 100) / (p.height || 1),
                                });
                            } else {
                                // Default back to rect if path not found
                                newObj = new fabric.Rect({
                                    ...commonProps,
                                    width: el.width || 100,
                                    height: el.height || 100,
                                    fill: shapeFill,
                                });
                            }
                        }
                    } else if (el.type === 'image') {
                        try {
                            const img = await fabric.FabricImage.fromURL(el.src);
                            img.set({
                                ...commonProps,
                                scaleX: (el.width || 100) / (img.width || 1),
                                scaleY: (el.height || 100) / (img.height || 1),
                            } as any);
                            newObj = img;
                        } catch (err) { console.error(err); }
                    }

                    if (newObj) {
                        canvas.add(newObj);
                    }
                }
            }

            canvas.renderAll();
            isInternalUpdate.current = false;
        };

        syncElements();
    }, [slide, canvasReady]);

    const selectedElement = slide.elements.find(el => el.id === selectedElementId);

    return (
        <div ref={containerRef} className="w-full h-full flex items-center justify-center overflow-hidden relative">
            <div
                className="border border-slate-200 overflow-hidden bg-white shadow-xl transition-all duration-300 ease-out shrink-0 relative"
                style={{
                    transform: `scale(${dynamicScale})`,
                    transformOrigin: 'center center'
                }}
            >
                <canvas ref={canvasRef} />

                {/* Empty Slide Helper Overlay */}
                {slide.elements.length === 0 && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none select-none animate-in fade-in duration-700">
                        <div className="flex flex-col items-center gap-4 text-slate-300">
                            {/* <div className="p-5 rounded-full bg-slate-50/50 border border-slate-100/50">
                                <Plus size={40} strokeWidth={1.5} className="opacity-50" />
                            </div> */}
                            <div className="flex flex-col items-center gap-1 text-center">
                                {/* <h3 className="text-lg font-semibold text-slate-400">Empty Slide</h3> */}
                                <h1 className="text-2xl font-medium text-slate-400">
                                    Click <span className="px-2 py-0.5 bg-slate-100 rounded-md text-slate-500 font-bold">/</span> for options
                                </h1>
                            </div>
                        </div>
                    </div>
                )}

                {/* Image Context Toolbar (Floating vertical list matching SlashMenu style) */}
                {selectedElement && selectedElement.type === 'image' && (
                    <div
                        className="absolute z-[5000] flex flex-col w-96 bg-white border border-slate-200 shadow-2xl rounded-2xl overflow-hidden animate-in fade-in zoom-in duration-200"
                        style={{
                            // Horizontal positioning: Left or Right of the image
                            left: selectedElement.x + (selectedElement.width / 2) > 600
                                ? `${selectedElement.x - 12}px`
                                : `${selectedElement.x + selectedElement.width + 12}px`,
                            top: `${selectedElement.y + (selectedElement.height / 2)}px`,
                            transform: selectedElement.x + (selectedElement.width / 2) > 600
                                ? 'translate(-100%, -50%)'
                                : 'translate(0, -50%)',
                        }}
                    >
                        <div className="px-5 py-3.5 text-sm uppercase tracking-wider font-bold text-slate-400 bg-slate-50/50 border-b border-slate-100">
                            Image Actions
                        </div>

                        <div className="p-2.5 flex flex-col gap-1">
                            {/* AI Generate */}
                            <button
                                onClick={(e) => { e.stopPropagation(); onAiGenerateClick(); }}
                                className="w-full flex items-center gap-5 px-5 py-4 text-left transition-all duration-150 rounded-xl hover:bg-slate-50 group"
                            >
                                <div className="p-3 rounded-xl bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                    <Sparkles size={28} />
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <span className="text-lg font-bold text-slate-900">AI Generate Image</span>
                                    <span className="text-sm text-slate-400 truncate leading-tight">Create an image with AI prompt</span>
                                </div>
                            </button>

                            {/* AI Generate Infographic */}
                            <button
                                onClick={(e) => { e.stopPropagation(); onAiGenerateInfographicClick(); }}
                                className="w-full flex items-center gap-5 px-5 py-4 text-left transition-all duration-150 rounded-xl hover:bg-slate-50 group"
                            >
                                <div className="p-3 rounded-xl bg-purple-50 text-purple-600 group-hover:bg-purple-600 group-hover:text-white transition-colors">
                                    <PieChart size={28} />
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <span className="text-lg font-bold text-slate-900">AI Infographic</span>
                                    <span className="text-sm text-slate-400 truncate leading-tight">Generate chart or diagram</span>
                                </div>
                            </button>

                            {/* Change Image */}
                            <button
                                onClick={(e) => { e.stopPropagation(); onUploadImageClick(); }}
                                className="w-full flex items-center gap-5 px-5 py-4 text-left transition-all duration-150 rounded-xl hover:bg-slate-50 group"
                            >
                                <div className="p-3 rounded-xl bg-slate-100 text-slate-500 group-hover:bg-slate-200 group-hover:text-slate-700 transition-colors">
                                    <ImageIcon size={28} />
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <span className="text-lg font-bold text-slate-700">Change Image</span>
                                    <span className="text-sm text-slate-400 truncate leading-tight">Replace with local file</span>
                                </div>
                            </button>

                            <div className="h-[1px] bg-slate-100 my-1 mx-3" />

                            {/* Delete */}
                            <button
                                onClick={(e) => { e.stopPropagation(); onDeleteElement(); }}
                                className="w-full flex items-center gap-5 px-5 py-4 text-left transition-all duration-150 rounded-xl hover:bg-red-50 group"
                            >
                                <div className="p-3 rounded-xl bg-red-50 text-red-500 group-hover:bg-red-500 group-hover:text-white transition-colors">
                                    <Trash2 size={28} />
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <span className="text-lg font-bold text-red-600">Delete Element</span>
                                    <span className="text-sm text-red-400/80 truncate leading-tight">Remove from slide</span>
                                </div>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
